import {
  Box, Flex, Stack, Text, useRadio, useRadioGroup, UseRadioGroupProps, UseRadioProps,
} from "@chakra-ui/react";
import { Immutable } from "@hookstate/core";
import { RadioOnIcon } from "../icons/RadioOnIcon.tsx";
import { RadioOffIcon } from "../icons/RadioOffIcon.tsx";
import { Option } from "../../types.ts";

interface RadioFilterProps<T> extends UseRadioGroupProps {
  value: Immutable<string | undefined>;
  options: Option<T>[];
  onChange: (nextValue: string) => void;
  /** Tinted row background for sidebar filters (default). Set false for popover usage. */
  tinted?: boolean;
}

export function RadioFilter<T extends string>({ value, options, onChange, tinted = true, ...props }: RadioFilterProps<T>) {
  if (!options.length) { console.warn("No options provided for radio filter"); return null; }
  const { getRadioProps } = useRadioGroup({ value: `${value}`, defaultValue: options[0].value, onChange });
  return (
    <Flex direction="column" borderRadius="8px" overflow="hidden">
      <Stack data-gc-ignore-input="1" className="ignore-radio" spacing={0}>
        {options.map((option) => (
          <CustomRadio key={option.value} tinted={tinted} {...getRadioProps({ value: option.value })} {...props}>
            <Text pointerEvents="none" fontFamily="Roboto" fontSize="14px" fontWeight="400" lineHeight="1.3" letterSpacing="0.5px" color={option.value === value ? "white" : "charcoal.200"} _groupHover={{ color: "white" }}>
              {option.label}
            </Text>
          </CustomRadio>
        ))}
      </Stack>
    </Flex>
  );
}

interface CustomRadioProps extends UseRadioProps { children: React.ReactNode; tinted?: boolean; }
function CustomRadio({ tinted = true, ...props }: CustomRadioProps) {
  const { state, getInputProps } = useRadio(props);
  const input = getInputProps();
  return (
    <Box
      display="flex" w="full" py="8px" px="8px" justifyContent="space-between" alignItems="center"
      as="label" cursor="pointer" role="group"
      {...(tinted
        ? { bg: "transparent.white.05", _hover: { bg: "transparent.white.10" } }
        : { layerStyle: "filterList" }
      )}
    >
      <input {...input} hidden />
      {props.children}
      {state.isChecked ? (
        <RadioOnIcon pointerEvents="none" color="blaze.300" boxSize="20px" />
      ) : (
        <RadioOffIcon pointerEvents="none" _groupHover={{ color: "transparent.white.80" }} color="transparent.white.40" boxSize="20px" />
      )}
    </Box>
  );
}
